import * as React from 'react';
import { TextInput as FlowBiteTextInput } from 'flowbite-react';
import { useRef, useState, useEffect, useCallback, useMemo, KeyboardEvent } from 'react';
import { cn } from '@restworld/utils-common';

export type OTPInputVariant = 'primary' | 'error';

export interface OTPInputProps {
  numberOfDigits?: number;
  onChange: (otp: number) => void;
  variant?: OTPInputVariant;
  value?: string;
  isSuccess: boolean;
  customTextFieldStyles?: string;
}

const generateEmptyArray = (length: number) => Array.from({ length }, () => '');

export const OTPInput = ({
  numberOfDigits = 6,
  onChange,
  variant = 'primary',
  value,
  isSuccess = false,
  customTextFieldStyles,
}: OTPInputProps) => {
  const inputRefs = useRef<HTMLInputElement[]>([]);

  const initialOtpDigits = useMemo(() => generateEmptyArray(numberOfDigits), [numberOfDigits]);
  const [otpDigits, setOtpDigits] = useState<string[]>(initialOtpDigits);

  useEffect(() => {
    const updatedOtpDigits = generateEmptyArray(numberOfDigits);
    if (value && !isNaN(Number(value))) {
      value.split('').forEach((digit, index) => {
        updatedOtpDigits[index] = digit;
      });
    }
    setOtpDigits(updatedOtpDigits);
  }, [numberOfDigits, value]);

  const handleInputChange = useCallback(
    (index: number, value: string) => {
      if (isNaN(Number(value))) return;

      const updatedOtpDigits = [...otpDigits];
      updatedOtpDigits[index] = value;
      setOtpDigits(updatedOtpDigits);

      if (value && index < numberOfDigits - 1) {
        inputRefs.current[index + 1]?.focus();
      }

      onChange(parseInt(updatedOtpDigits.join('')));
    },
    [otpDigits, numberOfDigits, onChange]
  );

  const handleInputPaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();
      const pasteData = e.clipboardData.getData('text/plain');
      if (!isNaN(Number(pasteData))) {
        const updatedOtpDigits = pasteData.split('').slice(0, numberOfDigits);
        setOtpDigits(updatedOtpDigits);
        onChange(parseInt(updatedOtpDigits.join('')));
      }
    },
    [numberOfDigits, onChange]
  );

  const handleInputKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>, index: number) => {
      if (e.key === 'Backspace') {
        const updatedDigits = [...otpDigits];
        updatedDigits[index] = '';
        setOtpDigits(updatedDigits);

        if (index > 0) {
          inputRefs.current[index - 1]?.focus();
        }
      }
    },
    [otpDigits]
  );

  return (
    <div className="flex flex-row justify-center gap-2 w-full">
      {otpDigits.map((digit, index) => (
        <TextField
          key={index}
          type="text"
          maxLength={1}
          value={digit}
          onChange={(e) => handleInputChange(index, e.target.value)}
          onPaste={handleInputPaste}
          onKeyDown={(e) => handleInputKeyDown(e, index)}
          ref={(input: HTMLInputElement) => {
            inputRefs.current[index] = input;
          }}
          variant={variant}
          isSuccess={isSuccess}
          $customTextFieldStyles={customTextFieldStyles}
        />
      ))}
    </div>
  );
};

const TextField = ({ variant, isSuccess, $customTextFieldStyles, className, ...props }: any) => (
  <FlowBiteTextInput
    className={cn(
      'flex outline-none',
      '[&_input]:outline-none [&_input]:box-border [&_input]:w-9.75 [&_input]:max-w-9.75 [&_input]:h-14.75 [&_input]:rounded-3.75 [&_input]:border-none [&_input]:focus:ring-gray-200 [&_input]:focus:bg-white [&_input]:font-normal [&_input]:text-base [&_input]:text-black [&_input]:text-center [&_input]:leading-5 [&_input]:bg-primary-50',
      variant === 'primary' && '[&_input]:placeholder:text-gray-400',
      variant === 'error' &&
        '[&_input]:!bg-red-50 [&_input]:placeholder:text-red-600 [&_input]:ring-1 [&_input]:ring-red-600 [&_input]:focus:ring-red-600',
      isSuccess &&
        '[&_input]:placeholder:text-green-500 [&_input]:ring-1 [&_input]:ring-green-500 [&_input]:focus:ring-green-500',
      $customTextFieldStyles &&
        $customTextFieldStyles
          .split(/\s+/)
          .filter(Boolean)
          .map((cls: string) => `[&_input]:${cls}`)
          .join(' '),
      className
    )}
    {...props}
  />
);
